import * as React from 'react'
import { RouteProps } from 'react-router'
import { Link } from 'react-router-dom'
import { Location } from 'history'
import { getMarketplaceGlobalsByKey } from '../DynamicLinks'

export interface MenuConfig extends RouteProps {
  defaultActiveKey: string
  mode?: 'DESKTOP' | 'WEB'
  menu: MenuItem[]
}

export interface MenuItem {
  type: 'PRIMARY' | 'SECONDARY' | 'LOGO'
  title?: string | React.ReactNode
  key: string
  icon: React.ReactNode
  url?: string
  callback?: () => void
  disabled?: boolean
}

export const getActiveItemKey = (menu: MenuItem[] = [], location?: Location<any>): string | null => {
  if (location && location.pathname) {
    const activeItem = menu.find(item => {
      const isExactRoot = location.pathname === '/' && item.url === '/'
      const isNotRootAndHasCommonPrefix = item.url && item.url !== '/' && location.pathname.startsWith(item.url)
      return isExactRoot || isNotRootAndHasCommonPrefix
    })

    if (activeItem && activeItem.url) {
      return activeItem.key
    }
  }
  return null
}

export const LinkItem: React.SFC<{
  item: MenuItem
  children: React.ReactNode
  activeItemRef?: React.RefObject<HTMLAnchorElement & Link>
}> = ({ item, children, activeItemRef }) => {
  return item.url ? (
    <Link
      ref={activeItemRef}
      className={`nav-item-link ${
        item.type === 'LOGO' ? 'is-logo' : item.type === 'SECONDARY' ? 'is-secondary' : 'is-primary'
      }`}
      to={item.url}
    >
      {children}
    </Link>
  ) : item.callback ? (
    <a
      ref={activeItemRef}
      className={`nav-item-link ${
        item.type === 'LOGO' ? 'is-logo' : item.type === 'SECONDARY' ? 'is-secondary' : 'is-primary'
      }`}
      onClick={item.callback}
    >
      {children}
    </a>
  ) : (
    <span
      className={`nav-item-link ${
        item.type === 'LOGO' ? 'is-logo' : item.type === 'SECONDARY' ? 'is-secondary' : 'is-primary'
      }`}
    >
      {children}
    </span>
  )
}

const MenuComponent: React.FC<MenuConfig> = ({ menu, location, mode: modeProp, defaultActiveKey }) => {
  const ownMode = getMarketplaceGlobalsByKey() ? 'DESKTOP' : 'WEB'
  // if pass mode, take that value
  // otherwise auto-detect
  const mode = modeProp ?? ownMode
  const activeItem = getActiveItemKey(menu, location)
  const [activeKey, setIsActive] = React.useState(activeItem || defaultActiveKey)
  const activeItemRef = React.createRef<HTMLAnchorElement & Link>()

  React.useEffect(() => {
    setIsActive(activeItem || defaultActiveKey)
  }, [activeItem])

  // Auto scroll to the position of active item
  React.useEffect(() => {
    const element = activeItemRef.current
    if (element) {
      element.scrollIntoView()
    }
  }, [activeKey])

  return (
    <nav className={`nav-bar ${mode === 'DESKTOP' ? 'is-desktop' : ''}`}>
      <ul>
        {menu.map((item: MenuItem) => {
          return item.disabled ? null : (
            <LinkItem activeItemRef={activeKey === item.key ? activeItemRef : undefined} key={item.key} item={item}>
              <li
                className={`nav-item ${activeKey === item.key ? 'is-active' : ''}`}
                onClick={() => item.type !== 'LOGO' && setIsActive(item.key)}
              >
                <div>{item.icon}</div>
                {item.title && <div className="nav-item-title">{item.title}</div>}
              </li>
            </LinkItem>
          )
        })}
      </ul>
    </nav>
  )
}

export const Menu = React.memo(MenuComponent)
